import type { User } from '@/types/user';

/**
 * What completed the member. `onboarding-completed` fires the moment the
 * completion criteria hold — the backend's rule, a primary coach on any
 * non-chatbot team (see RulesEngineContext) — and this says where the coach came
 * from:
 * - `fresh-complete` — this session assigned it: picked in coach matching, or
 *   auto-assigned by a group program's enrollment. The member finished
 *   onboarding just now.
 * - `already-complete` — the coach was already on the backend: the member
 *   completed earlier and reopened onboarding. The event fires when the
 *   resolver's teams fetch reveals the coach, which happens once org, profile
 *   and program pass — so a complete member who stops at eligibility or
 *   create-profile sends no event.
 *
 * Sent at most once per member per page load. Still not a dedup key across
 * sessions or devices, and a member who loses their coach and gets a new one is
 * `fresh-complete` again. Count completions downstream — the first
 * `onboarding-completed` per `uuid`, or the backend's coach-assignment time.
 */
export type OnboardingCompletionArrival = 'fresh-complete' | 'already-complete';

/**
 * `onboarding-completed` properties. Deliberately copies the cohort dimensions
 * `useAnalyticsIdentify` sets as person properties onto the event itself, so
 * downstream consumers (dbt, Braze) that read only event properties see the
 * values as of completion without a person join.
 *
 * `programUuid` comes from the member's current program instance — the GET on a
 * resume, the create POST on an in-flow enrollment — so `fresh-complete` and
 * `already-complete` rows both carry it. It is a required parameter so no call
 * site can forget it; it is only `undefined` if the backend returned an instance
 * without a program (the caller logs).
 */
export function buildOnboardingCompletedProperties(
  user: User,
  arrival: OnboardingCompletionArrival,
  programUuid: string | undefined
): Record<string, unknown> {
  const org = user.payingOrganization ?? user.customerProfile.ssoPayingOrganization;
  return {
    uuid: user.uuid,
    payingOrgUrn: org?.urn ?? null,
    payingOrgName: org?.name ?? null,
    isSsoUser: Boolean(user.customerProfile.ssoPayingOrganization),
    isTransitionMember: Boolean(user.customerProfile.isTransitionMember),
    isTester: user.isTester,
    teamCode: user.customerProfile.soloTeamCode ?? null,
    activeHighestTier: user.activeTierName ?? null,
    arrival,
    programUuid: programUuid ?? null,
  };
}
